import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/ui/table';
import { Button } from '@shadcn/components/ui/button';

import { Skeleton } from '@shadcn/components/ui/skeleton';
import { Card, CardContent } from '@shadcn/components/ui/card';
import {
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar,
  User,
  Package,
  Store,
  Hash
} from 'lucide-react';
import { Link } from "react-router-dom";
import { StatusBadge } from '../TicketSystem/StatusBadge';
import { TicketTypeIcon } from '../TicketSystem/TicketTypeIcon';
import { TICKET_TYPE_LABELS } from '../../constants';


const formatDate = (dateString) => {
  if (!dateString) return '—';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
};


const TicketTable = ({
  tickets = [],
  loading = false,
  onViewTicket,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  hasNextPage = false,
  hasPrevPage = false
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });


  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  /**
   * Get sort icon
   */
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" />
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  /**
   * Sort tickets
   */
  const sortedTickets = useMemo(() => {
    if (!sortConfig.key) return tickets;

    return [...tickets].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle nested properties
      if (sortConfig.key.includes('.')) {
        const keys = sortConfig.key.split('.');
        aValue = keys.reduce((obj, key) => obj?.[key], a);
        bValue = keys.reduce((obj, key) => obj?.[key], b);
      }

      // Handle dates
      if (sortConfig.key === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [tickets, sortConfig]);

  /**
   * Render loading skeleton
   */
  const renderLoadingSkeleton = () => (
    <>
      {[...Array(10)].map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton className="h-8 w-8 rounded-full" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-6 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-28" /></TableCell>
          <TableCell><Skeleton className="h-8 w-16" /></TableCell>
        </TableRow>
      ))}
    </>
  );

  /**
   * Render mobile card view
   */
  const renderMobileCard = (ticket) => (
    <Card key={ticket.id} className={`border ${ticket.closedAt ? 'opacity-50 bg-gray-50' : ''}`}>
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header with Type and Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TicketTypeIcon 
                type={ticket.ticketType} 
                size="sm"
                showBackground={true}
                className={ticket.closedAt  ? 'opacity-60' : ''}
              />
              <span className={`text-sm font-medium ${ticket.closedAt ? 'text-gray-500' : ''}`}>
                {TICKET_TYPE_LABELS[ticket.ticketType]}
              </span>
            </div>
            <StatusBadge 
              metaStatus={ticket.status?.metaStatus}
              originalStatus={ticket.status?.originalStatus}
              layout="horizontal"
              className={ticket.closedAt ? 'opacity-70' : ''}
            />
          </div>

          
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            <span className={`font-medium ${ticket.closedAt ? 'text-gray-500' : ''}`}>
              {ticket.customerName || 'Unknown Customer'}
            </span>
          </div>

          
          {ticket.batchCode && (
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-400" />
              <span className={`text-sm ${ticket.closedAt ? 'text-gray-500' : ''}`}>
                Batch: {ticket.batchCode}
              </span>
            </div>
          )}

          {/* Order and Store Info */}
          <div className="space-y-2">
            {ticket.orderNumber && (
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-gray-400" />
                <Link to={`/orders/${ticket.orderId}`} className="text-xs px-2 py-1 rounded bg-gray-100">
                  {ticket.orderNumber}
                </Link>
              </div>
            )}
            
            {ticket.storeName && (
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-gray-400" />
                <span className={`text-sm ${ticket.closedAt ? 'text-gray-500' : ''}`}>
                  {ticket.storeName}
                </span>
              </div>
            )}
          </div>

          {/* Footer with Date and Action */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div className={`flex items-center gap-2 text-xs text-gray-500 ${ticket.closedAt ? 'text-gray-400' : ''}`}>
              <Calendar className="w-3 h-3" />
              {formatDate(ticket.createdAt)}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewTicket?.(ticket)}
              className={`h-8 px-2 ${ticket.closedAt ? 'opacity-60' : ''}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  /**
   * Render empty state
   */
  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={8} className="text-center py-12">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Package className="w-12 h-12" />
          <div>
            <p className="text-lg font-medium">No tickets found</p>
            <p className="text-sm">Try adjusting your filters or search terms</p>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );

  /**
   * Generate page numbers for pagination
   */
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {

      const halfVisible = Math.floor(maxVisiblePages / 2);
      
      if (currentPage <= halfVisible + 1) {

        for (let i = 1; i <= maxVisiblePages - 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - halfVisible) {

        pages.push(1);
        pages.push('...');
        for (let i = totalPages - (maxVisiblePages - 2); i <= totalPages; i++) {
          pages.push(i);
        }
      } else {

        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  /**
   * Render pagination with page numbers
   */
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pageNumbers = generatePageNumbers();

    return (
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {/* First Page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={!hasPrevPage}
            className="hidden sm:inline-flex"
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>

          {/* Previous Page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPrevPage}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          {/* Page Numbers */}
          <div className="hidden sm:flex items-center gap-1">
            {pageNumbers.map((page, index) => {
              if (page === '...') {
                return (
                  <span key={`ellipsis-${index}`} className="px-2 py-1 text-sm text-muted-foreground">
                    ...
                  </span>
                );
              }
              
              return (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => onPageChange(page)}
                  className="min-w-[40px]"
                >
                  {page}
                </Button>
              );
            })}
          </div>

          {/* Next Page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNextPage}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>

          {/* Last Page */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(totalPages)}
            disabled={!hasNextPage}
            className="hidden sm:inline-flex"
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Mobile Card View */}
      <div className="block sm:hidden space-y-4">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : sortedTickets.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <Package className="w-12 h-12" />
              <div>
                <p className="text-lg font-medium">No tickets found</p>
                <p className="text-sm">Try adjusting your filters or search terms</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedTickets.map(renderMobileCard)}
          </div>
        )}
      </div>

      {/* Desktop/Tablet Table View */}
      <div className="hidden sm:block">
        <div className="overflow-x-auto">
          <Table className="min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer hover:bg-gray-50 w-[60px]"
                onClick={() => handleSort('ticketType')}
              >
                <div className="flex items-center gap-2">
                  Type
                  {getSortIcon('ticketType')}
                </div>
              </TableHead>
              
              <TableHead 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('customerName')}
              >
                <div className="flex items-center gap-2">
                  Customer
                  {getSortIcon('customerName')}
                </div>
              </TableHead>
              
              <TableHead 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('batchCode')}
              >
                <div className="flex items-center gap-2">
                  Batch Code
                  {getSortIcon('batchCode')}
                </div>
              </TableHead>
              
              <TableHead 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('orderNumber')}
              >
                <div className="flex items-center gap-2">
                  Order #
                  {getSortIcon('orderNumber')}
                </div>
              </TableHead>
              
              <TableHead 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('storeName')}
              >
                <div className="flex items-center gap-2">
                  Store
                  {getSortIcon('storeName')}
                </div>
              </TableHead>
              
              <TableHead 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('status.metaStatus')}
              >
                <div className="flex items-center gap-2">
                  Status
                  {getSortIcon('status.metaStatus')}
                </div>
              </TableHead>
              
              <TableHead 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center gap-2">
                  Created
                  {getSortIcon('createdAt')}
                </div>
              </TableHead>
              
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          
          <TableBody>
            {loading ? (
              renderLoadingSkeleton()
            ) : sortedTickets.length === 0 ? (
              renderEmptyState()
            ) : (
              sortedTickets.map((ticket) => {
                
                const isClosedTicket = ticket.closedAt;
                
                return (
                <TableRow 
                  key={ticket.id} 
                  className={`hover:bg-gray-50 ${isClosedTicket ? 'opacity-50 bg-gray-50 text-gray-500' : ''}`}
                >
                  <TableCell>
                    <TicketTypeIcon 
                      type={ticket.ticketType} 
                      size="md"
                      showBackground={true}
                      className={isClosedTicket ? 'opacity-60' : ''}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className={`w-4 h-4 ${isClosedTicket ? 'text-gray-400' : 'text-gray-400'}`} />
                      <span className={`font-medium ${isClosedTicket ? 'text-gray-500' : ''}`}>
                        {ticket.customerName || '—'}
                      </span>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Package className={`w-4 h-4 ${isClosedTicket ? 'text-gray-400' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isClosedTicket ? 'text-gray-500' : ''}`}>
                        {ticket.batchCode || '—'}
                      </span>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    {ticket.orderNumber ? (
                      <Link to={`/orders/${ticket.orderId}`} className="text-xs px-2 py-1 rounded bg-gray-100">
                       <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                        {ticket.orderNumber}
                        </code>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    <span className={`text-sm ${isClosedTicket ? 'text-gray-500' : ''}`}>
                      {ticket.storeName || '—'}
                    </span>
                  </TableCell>
                  
                  <TableCell>
                    <StatusBadge 
                      metaStatus={ticket.status?.metaStatus}
                      originalStatus={ticket.status?.originalStatus}
                      layout="vertical"
                      className={isClosedTicket ? 'opacity-70' : ''}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <div className={`flex items-center gap-2 text-sm text-muted-foreground ${isClosedTicket ? 'text-gray-400' : ''}`}>
                      <Calendar className="w-4 h-4" />
                      {formatDate(ticket.createdAt)}
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewTicket?.(ticket)}
                      className={isClosedTicket ? 'opacity-60' : ''}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              )
              })
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default TicketTable;
